import { cn } from '@/lib/utils/cn'
import { formatPercent } from '@/lib/utils/format'

interface Segment {
  key: string
  label: string
  count: number
  className: string
  hint: string
}

/**
 * The run's result as one bar.
 *
 * Three outcomes on a single 100%-wide bar rather than three separate tiles, because they
 * are parts of one whole and the eye should read the proportions immediately. The order is
 * fixed — correct, wrong tool, tool failed, no tool — so two runs can be compared by shape.
 */
export function OutcomeBar({
  correct,
  wrongTool,
  toolFailed,
  noTool,
  total,
  className,
}: {
  correct: number
  wrongTool: number
  toolFailed: number
  noTool: number
  total: number
  className?: string
}) {
  const segments: Segment[] = [
    {
      key: 'correct',
      label: 'correct',
      count: correct,
      className: 'bg-ok',
      hint: 'Expected tool chosen and executed successfully.',
    },
    {
      key: 'wrong',
      label: 'wrong tool',
      count: wrongTool,
      className: 'bg-miss',
      hint: 'A different tool was chosen. Fix the tool descriptions.',
    },
    {
      key: 'failed',
      label: 'tool failed',
      count: toolFailed,
      className: 'bg-fail',
      hint: 'Right tool, failed execution. Fix the code or credentials.',
    },
    {
      key: 'none',
      label: 'no tool',
      count: noTool,
      className: 'bg-denied',
      hint: 'The model answered without calling a tool.',
    },
  ].filter((segment) => segment.count > 0)

  const safeTotal = Math.max(total, 1)

  return (
    <div className={cn('px-4 py-4', className)}>
      <div className="flex h-2.5 overflow-hidden rounded-sm bg-raised">
        {segments.map((segment) => (
          <div
            key={segment.key}
            className={cn('h-full origin-left animate-grow', segment.className)}
            style={{ width: `${(segment.count / safeTotal) * 100}%` }}
            title={`${segment.count} ${segment.label} — ${segment.hint}`}
          />
        ))}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5">
        {segments.map((segment) => (
          <span key={segment.key} className="flex items-center gap-1.5 text-xs" title={segment.hint}>
            <span className={cn('h-1.5 w-4 rounded-sm', segment.className)} aria-hidden="true" />
            <span className="text-ink-dim">{segment.label}</span>
            <span className="mono text-ink-muted">
              {segment.count} ({formatPercent(segment.count / safeTotal, 0)})
            </span>
          </span>
        ))}
      </div>
    </div>
  )
}
